import { useState } from 'react';
import {
  AlertTriangle, AtSign, Copy, Database, Download, Loader2, Pin, PinOff, RotateCcw, Sparkles, ThumbsDown, ThumbsUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import AiActionCard from './AiActionCard';
import AiMarkdown from './AiMarkdown';
import { asOfLabel, describeSources, extractTable, providerLabel, tableToCsv } from './aiModel';
import { Avatar, IconTile, saveText } from './ui';

export function AssistantTile({ tone = 'lime', icon = Sparkles }) {
  return <IconTile icon={icon} tone={tone} size={28} className="mt-0.5" />;
}

/** "@ LIV Waterside" — the project a question was asked about (25 idea 7). */
export function ScopeTag({ name, className }) {
  return (
    <span className={cn('inline-flex max-w-full items-center gap-0.5 rounded-md border border-[color:var(--brand-soft-border)] bg-surface px-1.5 py-px align-[1px] text-[12px] font-semibold text-brand-ink', className)}>
      <AtSign className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{name}</span>
    </span>
  );
}

export function UserMessage({ message, userName, onAskAgain }) {
  const time = formatTime(message.created_at, { fallback: '' });
  const scopeName = message.scope?.project_name;
  return (
    <div className="flex flex-col items-end gap-1" data-testid="ai-message-user" data-stopped={message.stopped ? 'true' : undefined}>
      <div className="flex max-w-full items-end justify-end gap-2.5">
        {time ? <span className="mb-1 shrink-0 text-[11.5px] text-ink-3">{time}</span> : null}
        <div className={cn(
          'max-w-[min(560px,85%)] whitespace-pre-wrap break-words rounded-[14px_14px_4px_14px] border border-[color:var(--brand-soft-border)] bg-brand-soft px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-ink',
          message.stopped && 'opacity-70'
        )}>
          {scopeName ? <><ScopeTag name={scopeName} className="mr-1.5" /></> : null}
          {message.content}
        </div>
        <Avatar name={userName} size={24} className="mb-0.5" />
      </div>
      {message.stopped ? (
        <p className="mr-[34px] text-[12px] text-ink-3" data-testid="ai-message-stopped">
          Stopped — no answer was saved.{' '}
          {onAskAgain ? (
            <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => onAskAgain(message)}>
              Ask again
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function FooterButton({ label, icon: Icon, onClick, pressed, children, testId }) {
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      data-testid={testId}
      className={cn('text-ink-3 hover:text-ink', pressed && 'bg-brand-soft text-brand-ink hover:text-brand-ink')}
    >
      <Icon aria-hidden="true" />
      {children}
    </Button>
  );
}

/**
 * One answer: text, action result cards, and the source line
 * ("Based on 128 invoices · 63 projects · as of 21:04 · Gemini" + Copy, CSV, 👍 👎).
 */
export function AssistantMessage({
  message, onFeedback, onUndoAction, onConfirmAction, onCancelAction, onAskAgain, canAssign, onRetry, onPin, pinned,
}) {
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const actions = Array.isArray(message.actions) ? message.actions : [];
  const table = extractTable(message.content);
  const sources = describeSources(message);
  const asOf = asOfLabel(message.created_at);
  const provider = providerLabel(message.provider);

  if (message.error) {
    return (
      <div className="flex gap-3" data-testid="ai-message-error">
        <AssistantTile tone="red" icon={AlertTriangle} />
        <div className="min-w-0 max-w-[720px] rounded-[14px] border border-[color:var(--status-red-bg)] bg-surface px-4 py-3">
          <p className="text-[13.5px] font-semibold text-status-red-ink">{message.errorTitle || 'Something went wrong'}</p>
          <p className="mt-0.5 text-[13px] text-ink-2">{message.content}</p>
          {onRetry && message.retry ? (
            <Button type="button" size="sm" variant="outline" className="mt-2.5" onClick={onRetry}>
              <RotateCcw aria-hidden="true" />
              Try again
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(message.content || ''));
      notify.info('Answer copied');
    } catch {
      notify.error('Could not copy the answer');
    }
  };

  const exportCsv = () => {
    const stamp = (message.created_at || new Date().toISOString()).slice(0, 16).replace(/[-:T]/g, '');
    saveText(tableToCsv(table), `ai-answer-${stamp}.csv`);
  };

  const feedback = async (value) => {
    if (!onFeedback || feedbackBusy) return;
    setFeedbackBusy(true);
    try {
      await onFeedback(message, message.feedback === value ? null : value);
    } catch (err) {
      notify.error('Could not save your feedback', { description: getErrorMessage(err) });
    } finally {
      setFeedbackBusy(false);
    }
  };

  const canRate = Boolean(message.id && onFeedback);
  // Pin to Dashboard (25 idea 10): answers that only read data — never one that made or proposed a change, nor
  // the app's reply to a change request without a card ("which invoice do you mean?", provider "app"); the
  // server refuses those too (POST /ai/pins).
  const canPin = Boolean(onPin && message.id && !message.error && !actions.length && message.provider !== 'app');
  const togglePin = async () => {
    if (pinBusy) return;
    setPinBusy(true);
    try {
      await onPin(message, Boolean(pinned));
    } finally {
      setPinBusy(false);
    }
  };
  // A change (proposal or result) reads as one line above its card, like a person saying it.
  const compact = actions.length > 0 && !table;

  const cards = actions.map((action, index) => (
    <div key={`${action.type}-${index}`} className="max-w-[640px]">
      <AiActionCard
        action={action}
        canAssign={canAssign}
        canUndo={Boolean(message.id && action.undo_id && onUndoAction)}
        onUndo={() => onUndoAction(message, index)}
        onConfirm={() => onConfirmAction(message, index)}
        onCancel={() => onCancelAction(message, index)}
        onAskAgain={onAskAgain}
      />
    </div>
  ));

  if (compact) {
    // Every change of this answer was undone: "Done — …" above the card would contradict it, so it is muted and
    // followed by when it was undone.
    const undoneAt = actions.every((action) => action?.undone_at)
      ? actions.map((action) => String(action.undone_at)).sort().pop()
      : '';
    const undoneTime = undoneAt ? formatTime(undoneAt, { fallback: '' }) : '';
    return (
      <div className="flex gap-3" data-testid="ai-message-assistant">
        <AssistantTile />
        <div className="min-w-0 flex-1 space-y-2.5 pt-1">
          <div className={cn('max-w-[720px]', undoneAt && 'opacity-60')} data-undone={undoneAt ? 'true' : undefined}>
            <AiMarkdown text={message.content} projectRefs={message.project_refs} />
          </div>
          {undoneAt ? (
            <p className="-mt-1 text-[12px] font-semibold text-ink-3" data-testid="ai-message-undone">
              Undone{undoneTime ? ` at ${undoneTime}` : ''} — nothing was changed in the end.
            </p>
          ) : null}
          {cards}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3" data-testid="ai-message-assistant">
      <AssistantTile />
      <div className="min-w-0 flex-1 space-y-2.5">
        <div className="max-w-[760px] rounded-[14px] border border-line bg-surface px-[18px] pb-2.5 pt-3.5">
          <AiMarkdown text={message.content} projectRefs={message.project_refs} />
          {message.welcome ? null : (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-t border-line pt-2.5" data-testid="ai-answer-footer">
              <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-ink-3" title={sources.detail || undefined}>
                <Database className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
                <span>{[sources.text, asOf].filter(Boolean).join(' · ') || 'Answer'}</span>
                {provider ? (
                  <span className="inline-flex h-5 items-center rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-semibold text-ink-2" title={message.model || undefined}>
                    {provider}
                  </span>
                ) : null}
              </p>
              <div className="flex flex-wrap items-center gap-0.5">
                <FooterButton label="Copy answer" icon={Copy} onClick={copy} testId="ai-answer-copy">Copy</FooterButton>
                {table ? <FooterButton label="Download the table as CSV" icon={Download} onClick={exportCsv} testId="ai-answer-csv">CSV</FooterButton> : null}
                {canRate ? (
                  <>
                    <FooterButton label="Good answer" icon={ThumbsUp} pressed={message.feedback === 'up'} onClick={() => feedback('up')} testId="ai-answer-up" />
                    <FooterButton label="Bad answer" icon={ThumbsDown} pressed={message.feedback === 'down'} onClick={() => feedback('down')} testId="ai-answer-down" />
                  </>
                ) : null}
                {canPin ? (
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={togglePin}
                    disabled={pinBusy}
                    aria-pressed={Boolean(pinned)}
                    title={pinned ? 'On your Dashboard — refreshed every morning. Click to unpin.' : 'Show this question on your Dashboard, answered again every morning'}
                    className={cn('ml-1', pinned && 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink hover:bg-brand-soft')}
                    data-testid="ai-answer-pin"
                  >
                    {pinBusy ? <Loader2 className="animate-spin" aria-hidden="true" /> : pinned ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
                    {pinned ? 'Pinned' : 'Pin to Dashboard'}
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </div>
        {cards}
      </div>
    </div>
  );
}
